import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import {
  useLocation
} from "react-router-dom";

import {
  t
} from "../services";

import {
  UPDATE_MENU_EVENT,
  UPDATE_RIBBON_CONTEXT_EVENT
} from "../events/Gui";

import {
  useConstructor
} from "../utils";

import styles from
  "./DesktopMenu.module.styl";


const entries = (value) =>
  Object.entries(
    value || {}
  );


const getInitialKey = (
  menu
) =>
  Object.keys(
    menu || {}
  )[0] || null;


const normalizePath = (
  path
) => {
  if (!path) {
    return "";
  }

  if (path === "/") {
    return "/";
  }

  return path.endsWith("/")
    ? path.slice(0, -1)
    : path;
};


const pathMatchScore = (
  descriptor,
  pathname
) => {
  if (!descriptor) {
    return -1;
  }

  if (
    typeof descriptor.match
      === "function"
  ) {
    return descriptor.match(
      pathname
    )
      ? 10_000
      : -1;
  }

  if (!descriptor.path) {
    return -1;
  }

  const target =
    normalizePath(
      descriptor.path
    );

  const current =
    normalizePath(
      pathname
    );

  if (!target) {
    return -1;
  }

  if (current === target) {
    return 9_000
      + target.length;
  }

  if (
    target !== "/"
    && current.startsWith(
      target + "/"
    )
  ) {
    return target.length;
  }

  return -1;
};


const labelOf = (
  descriptor
) => {
  if (!descriptor?.label) {
    return "";
  }

  return descriptor.rawLabel
    ? descriptor.label
    : t(descriptor.label);
};


const joinKey = (
  parentKey,
  childKey
) =>
  parentKey
    ? `${parentKey}.${childKey}`
    : childKey;


/**
 * Recursively searches one menu branch for
 * the descriptor matching the current route.
 *
 * keyPath is relative to the top-level section,
 * so it matches the item keys used by the renderer.
 */
const resolveDescriptor = (
  descriptor,
  pathname,
  keyPath
) => {
  let best = {
    score:
      pathMatchScore(
        descriptor,
        pathname
      ),

    keyPath
  };

  for (
    const [
      childKey,
      child
    ]
    of entries(
      descriptor?.options
    )
  ) {
    const candidate =
      resolveDescriptor(
        child,
        pathname,
        joinKey(
          keyPath,
          childKey
        )
      );

    if (
      candidate.score
        > best.score
    ) {
      best =
        candidate;
    }
  }

  return best;
};


const resolveActive = (
  rootMenu,
  pathname,
  fallbackKey,
  contextOptions
) => {
  let selectedMenuKey =
    fallbackKey;

  let selectedActionKey =
    null;

  let bestScore =
    -1;

  for (
    const [
      menuKey,
      section
    ]
    of entries(rootMenu)
  ) {
    const sectionScore =
      pathMatchScore(
        section,
        pathname
      );

    if (
      sectionScore > bestScore
    ) {
      bestScore =
        sectionScore;

      selectedMenuKey =
        menuKey;

      selectedActionKey =
        null;
    }

    for (
      const [
        optionKey,
        descriptor
      ]
      of entries(
        section?.options
      )
    ) {
      const candidate =
        resolveDescriptor(
          descriptor,
          pathname,
          optionKey
        );

      if (
        candidate.score
          > bestScore
      ) {
        bestScore =
          candidate.score;

        selectedMenuKey =
          menuKey;

        selectedActionKey =
          candidate.keyPath;
      }
    }
  }

  for (
    const [
      optionKey,
      descriptor
    ]
    of entries(
      contextOptions
    )
  ) {
    const candidate =
      resolveDescriptor(
        descriptor,
        pathname,
        `context:${optionKey}`
      );

    if (
      candidate.score
        > bestScore
    ) {
      bestScore =
        candidate.score;

      selectedActionKey =
        candidate.keyPath;
    }
  }

  return {
    menuKey:
      selectedMenuKey,

    actionKey:
      selectedActionKey
  };
};


const isKeyActive = (
  activeKey,
  itemKey
) =>
  activeKey === itemKey
  || (
    activeKey
    && activeKey.startsWith(
      itemKey + "."
    )
  );


const executeDescriptor = (
  descriptor,
  gui
) => {
  if (descriptor.path) {
    gui.goTo(
      descriptor.path
    );

    return true;
  }

  if (descriptor.do) {
    descriptor.do(gui);

    return true;
  }

  return false;
};


export const DesktopMenuItem = ({
  itemKey,
  descriptor,
  gui,
  active,
  onSelect
}) => {
  const execute = () => {
    onSelect(
      itemKey,
      descriptor
    );

    executeDescriptor(
      descriptor,
      gui
    );
  };

  return (
    <button
      type="button"
      role="menuitem"
      className={
        active
          ? styles.menuItemActive
          : styles.menuItem
      }
      title={
        labelOf(descriptor)
      }
      onClick={execute}
    >
      <span
        className={
          styles.itemIndicator
        }
      >
        {active ? "✓" : ""}
      </span>

      <span
        className={
          styles.itemIcon
        }
      >
        {descriptor.icon || null}
      </span>

      <span
        className={
          styles.itemLabel
        }
      >
        {labelOf(descriptor)}
      </span>
    </button>
  );
};


/**
 * Recursive menu node.
 *
 * A descriptor with options is rendered as a submenu.
 * A descriptor without options is rendered as a normal
 * actionable DesktopMenuItem.
 */
const DesktopMenuNode = ({
  itemKey,
  descriptor,
  gui,
  activeActionKey,
  onSelect
}) => {
  const [
    open,
    setOpen
  ] = useState(false);

  const children =
    entries(
      descriptor?.options
    );

  const hasChildren =
    children.length > 0;

  const active =
    isKeyActive(
      activeActionKey,
      itemKey
    );

  useEffect(() => {
    if (
      active
      && hasChildren
    ) {
      setOpen(true);
    }
  }, [
    active,
    hasChildren
  ]);

  if (!hasChildren) {
    return (
      <DesktopMenuItem
        itemKey={itemKey}
        descriptor={descriptor}
        gui={gui}
        active={active}
        onSelect={onSelect}
      />
    );
  }

  const toggle = (
    event
  ) => {
    event.stopPropagation();

    setOpen(
      current =>
        !current
    );
  };

  return (
    <div
      role="none"
      className={
        styles.menuNode
      }
      onMouseEnter={() =>
        setOpen(true)
      }
      onMouseLeave={() =>
        setOpen(false)
      }
    >
      <button
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          active
            ? styles.menuItemActive
            : styles.menuItem
        }
        title={
          labelOf(descriptor)
        }
        onClick={toggle}
      >
        <span
          className={
            styles.itemIndicator
          }
        >
          {active ? "✓" : ""}
        </span>

        <span
          className={
            styles.itemIcon
          }
        >
          {descriptor.icon || null}
        </span>

        <span
          className={
            styles.itemLabel
          }
        >
          {labelOf(descriptor)}
        </span>

        <span
          className={
            styles.itemArrow
          }
          aria-hidden="true"
        >
          ›
        </span>
      </button>

      {open && (
        <div
          className={
            `${styles.dropdown} ${styles.submenuDropdown}`
          }
          role="menu"
        >
          {children.map(
            ([
              childKey,
              childDescriptor
            ]) => {
              const childItemKey =
                joinKey(
                  itemKey,
                  childKey
                );

              return (
                <DesktopMenuNode
                  key={
                    childItemKey
                  }
                  itemKey={
                    childItemKey
                  }
                  descriptor={
                    childDescriptor
                  }
                  gui={gui}
                  activeActionKey={
                    activeActionKey
                  }
                  onSelect={
                    onSelect
                  }
                />
              );
            }
          )}
        </div>
      )}
    </div>
  );
};


export const DesktopMenu = ({
  gui,
  sections
}) => {
  const location =
    useLocation();

  const rootMenu =
    sections || {};

  const rootMenuRef =
    useRef(rootMenu);

  const containerRef =
    useRef(null);

  const initialKey =
    useMemo(
      () =>
        getInitialKey(
          rootMenu
        ),
      [rootMenu]
    );

  const [
    forcedMenuKey,
    setForcedMenuKey
  ] = useState(null);

  const [
    openMenuKey,
    setOpenMenuKey
  ] = useState(null);

  const [
    commandActiveKey,
    setCommandActiveKey
  ] = useState(null);

  const [
    contextOptions,
    setContextOptions
  ] = useState({});


  useEffect(() => {
    rootMenuRef.current =
      rootMenu;
  }, [rootMenu]);


  useEffect(() => {
    if (
      forcedMenuKey
      && !rootMenu[forcedMenuKey]
    ) {
      setForcedMenuKey(null);
      setOpenMenuKey(null);
      setCommandActiveKey(null);
    }
  }, [
    rootMenu,
    forcedMenuKey
  ]);


  const active =
    useMemo(
      () =>
        resolveActive(
          rootMenu,
          location.pathname,
          forcedMenuKey
            || initialKey,
          contextOptions
        ),
      [
        rootMenu,
        location.pathname,
        forcedMenuKey,
        initialKey,
        contextOptions
      ]
    );


  const activeMenuKey =
    active.menuKey
    || initialKey;


  const activeActionKey =
    active.actionKey
    || commandActiveKey;


  useConstructor(() => {
    UPDATE_MENU_EVENT.subscribe(
      ({ k }) => {
        if (
          k
          && rootMenuRef.current[k]
        ) {
          setForcedMenuKey(k);
          setCommandActiveKey(null);
        }
      }
    );

    UPDATE_RIBBON_CONTEXT_EVENT
      .subscribe(
        (payload = {}) => {
          if (payload.clear) {
            setContextOptions({});
            setCommandActiveKey(null);

            return;
          }

          setContextOptions(
            payload.options || {}
          );

          setCommandActiveKey(null);
        }
      );
  });


  useEffect(() => {
    setCommandActiveKey(null);
    setOpenMenuKey(null);
  }, [location.pathname]);


  useEffect(() => {
    const closeOnOutsideClick = (
      event
    ) => {
      if (
        !containerRef.current
          ?.contains(event.target)
      ) {
        setOpenMenuKey(null);
      }
    };

    const closeOnEscape = (
      event
    ) => {
      if (
        event.key === "Escape"
      ) {
        setOpenMenuKey(null);
      }
    };

    document.addEventListener(
      "mousedown",
      closeOnOutsideClick
    );

    document.addEventListener(
      "keydown",
      closeOnEscape
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        closeOnOutsideClick
      );

      document.removeEventListener(
        "keydown",
        closeOnEscape
      );
    };
  }, []);


  const selectItem = (
    menuKey,
    itemKey,
    descriptor
  ) => {
    setForcedMenuKey(
      menuKey
    );

    setOpenMenuKey(null);

    if (
      descriptor.path
      || descriptor.match
    ) {
      setCommandActiveKey(null);

      return;
    }

    setCommandActiveKey(
      itemKey
    );
  };


  if (!activeMenuKey) {
    return null;
  }


  return (
    <nav
      ref={containerRef}
      className={styles.container}
      aria-label="Application menu"
    >
      <div
        className={styles.menuBar}
        role="menubar"
      >
        {entries(rootMenu).map(
          ([
            menuKey,
            section
          ]) => {
            const regularItems =
              entries(
                section?.options
              );

            const contextualItems =
              menuKey
                === activeMenuKey
                ? entries(
                    contextOptions
                  )
                : [];

            const hasDropdown =
              regularItems.length > 0
              || contextualItems.length > 0;

            const isOpen =
              menuKey
                === openMenuKey;

            const isActive =
              menuKey
                === activeMenuKey;


            const runDirectAction =
              () => {
                setForcedMenuKey(
                  menuKey
                );

                setOpenMenuKey(null);
                setCommandActiveKey(null);

                executeDescriptor(
                  section,
                  gui
                );
              };


            const toggleMenu = () => {
              setForcedMenuKey(
                menuKey
              );

              setCommandActiveKey(
                null
              );

              if (!hasDropdown) {
                runDirectAction();

                return;
              }

              setOpenMenuKey(
                isOpen
                  ? null
                  : menuKey
              );
            };


            return (
              <div
                key={menuKey}
                className={
                  styles.menu
                }
                onMouseEnter={() => {
                  if (
                    openMenuKey
                    && openMenuKey
                      !== menuKey
                  ) {
                    setOpenMenuKey(
                      hasDropdown
                        ? menuKey
                        : null
                    );

                    setForcedMenuKey(
                      menuKey
                    );
                  }
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  aria-haspopup={
                    hasDropdown
                      ? "menu"
                      : undefined
                  }
                  aria-expanded={
                    hasDropdown
                      ? isOpen
                      : undefined
                  }
                  className={
                    isOpen
                      ? styles.menuButtonOpen
                      : isActive
                        ? styles.menuButtonActive
                        : styles.menuButton
                  }
                  onClick={
                    toggleMenu
                  }
                >
                  {labelOf(section)}
                </button>

                {isOpen
                  && hasDropdown
                  && (
                    <div
                      className={
                        styles.dropdown
                      }
                      role="menu"
                    >
                      {regularItems.map(
                        ([
                          itemKey,
                          descriptor
                        ]) => (
                          <DesktopMenuNode
                            key={
                              itemKey
                            }
                            itemKey={
                              itemKey
                            }
                            descriptor={
                              descriptor
                            }
                            gui={gui}
                            activeActionKey={
                              activeActionKey
                            }
                            onSelect={(
                              nextItemKey,
                              nextDescriptor
                            ) =>
                              selectItem(
                                menuKey,
                                nextItemKey,
                                nextDescriptor
                              )
                            }
                          />
                        )
                      )}

                      {contextualItems.length > 0
                        && (
                          <>
                            {regularItems.length > 0
                              && (
                                <div
                                  className={
                                    styles.separator
                                  }
                                  role="separator"
                                />
                              )}

                            {contextualItems.map(
                              ([
                                itemKey,
                                descriptor
                              ]) => {
                                const contextualKey =
                                  `context:${itemKey}`;

                                return (
                                  <DesktopMenuNode
                                    key={
                                      contextualKey
                                    }
                                    itemKey={
                                      contextualKey
                                    }
                                    descriptor={
                                      descriptor
                                    }
                                    gui={gui}
                                    activeActionKey={
                                      activeActionKey
                                    }
                                    onSelect={(
                                      nextItemKey,
                                      nextDescriptor
                                    ) =>
                                      selectItem(
                                        menuKey,
                                        nextItemKey,
                                        nextDescriptor
                                      )
                                    }
                                  />
                                );
                              }
                            )}
                          </>
                        )}
                    </div>
                  )}
              </div>
            );
          }
        )}
      </div>
    </nav>
  );
};


export const RibbonMenu =
  DesktopMenu;

export default DesktopMenu;